import React from 'react';
import { CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import type { MachineStatus } from '../../types';

export interface StatusBadgeProps {
  status: MachineStatus | string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showPulse?: boolean;
  language?: 'th' | 'en' | 'both';
  className?: string;
}

export function StatusBadge({
  status,
  size = 'md',
  showIcon = true,
  showPulse = true,
  language = 'both',
  className = '',
}: StatusBadgeProps) {
  const normStatus = (status || 'NORMAL').toUpperCase() as MachineStatus;

  // Configuration for each status
  const config = {
    NORMAL: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      dot: 'bg-emerald-500',
      pulse: 'bg-emerald-400',
      icon: CheckCircle2,
      labelTh: 'ปกติ',
      labelEn: 'Up-to-Date',
    },
    DUE_SOON: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      dot: 'bg-amber-500',
      pulse: 'bg-amber-400',
      icon: Clock,
      labelTh: 'ใกล้ครบกำหนด',
      labelEn: 'Due Soon',
    },
    OVERDUE: {
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      text: 'text-rose-700',
      dot: 'bg-rose-500',
      pulse: 'bg-rose-400',
      icon: AlertTriangle,
      labelTh: 'เกินกำหนด',
      labelEn: 'Overdue',
    },
  }[normStatus] || {
    bg: 'bg-slate-100',
    border: 'border-slate-200',
    text: 'text-slate-700',
    dot: 'bg-slate-400',
    pulse: 'bg-slate-300',
    icon: CheckCircle2,
    labelTh: 'ไม่ระบุ',
    labelEn: 'Unknown',
  };

  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-xs sm:text-sm gap-1.5',
    lg: 'px-3.5 py-1.5 text-sm sm:text-base gap-2 font-bold',
  }[size];

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  }[size];

  // Label text determination
  let labelText = '';
  if (language === 'th') {
    labelText = config.labelTh;
  } else if (language === 'en') {
    labelText = config.labelEn;
  } else {
    // 'both'
    labelText = `${config.labelTh} (${config.labelEn})`;
  }

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold border shadow-sm transition-colors ${config.bg} ${config.border} ${config.text} ${sizeClasses} ${className}`}
      role="status"
      aria-label={`Status: ${config.labelEn}`}
    >
      {/* Pulse dot for alert statuses */}
      {showPulse && (normStatus === 'OVERDUE' || normStatus === 'DUE_SOON') && (
        <span className="relative flex h-2 w-2">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${config.pulse}`}
          />
          <span className={`relative inline-flex rounded-full h-2 w-2 ${config.dot}`} />
        </span>
      )}

      {showIcon && <Icon className={`${iconSizes} flex-shrink-0`} />}
      <span className="truncate">{labelText}</span>
    </span>
  );
}
